/**
 * NIB Category Helper Functions
 *
 * Utility functions for working with NIB categories, rates, and calculations.
 * All values reflect the July 2024 NIB contribution system updates.
 */

import {
  NIBCategory,
  NIBPayIndicator,
  NIB_CATEGORY_INFO,
  RETIRED_CATEGORIES,
  ZERO_EMPLOYEE_RATE_CATEGORIES,
  NIB_VALIDATION,
} from '@/types';

// ==================== Rate Helpers ====================

/**
 * Get contribution rates for a specific category
 *
 * @param category - NIB category (1-7)
 * @returns Object with employee, employer, and total rates
 * @throws Error if category is invalid
 *
 * @example
 * const rates = getRatesForCategory(NIBCategory.REGULAR);
 * // { employeeRate: 0.0465, employerRate: 0.0665, totalRate: 0.113 }
 */
export function getRatesForCategory(category: NIBCategory): {
  employeeRate: number;
  employerRate: number;
  totalRate: number;
} {
  const categoryInfo = NIB_CATEGORY_INFO[category];
  if (!categoryInfo) {
    throw new Error(`Invalid category: ${category}. Must be 1-7.`);
  }

  return {
    employeeRate: categoryInfo.employeeRate,
    employerRate: categoryInfo.employerRate,
    totalRate: categoryInfo.totalRate,
  };
}

// ==================== Wage Cap Helpers ====================

/**
 * Get wage cap for a specific category and pay period
 *
 * @param category - NIB category (1-7)
 * @param payIndicator - Pay frequency ('W' for weekly, 'M' for monthly)
 * @returns The wage cap amount in dollars
 * @throws Error if category is invalid
 *
 * @example
 * const cap = getWageCapForCategory(NIBCategory.REGULAR, 'W');
 * // 810.0
 */
export function getWageCapForCategory(
  category: NIBCategory,
  payIndicator: NIBPayIndicator
): number {
  const categoryInfo = NIB_CATEGORY_INFO[category];
  if (!categoryInfo) {
    throw new Error(`Invalid category: ${category}. Must be 1-7.`);
  }

  return payIndicator === 'W'
    ? categoryInfo.weeklyCap
    : categoryInfo.monthlyCap;
}

/**
 * Calculate the total wage cap for a pay period
 *
 * @param category - NIB category (1-7)
 * @param payIndicator - Pay frequency ('W' for weekly, 'M' for monthly)
 * @param weeksWorked - Number of weeks in the period
 * @returns Total wage cap for the period
 *
 * @example
 * const totalCap = calculatePeriodWageCap(NIBCategory.REGULAR, 'W', 4);
 * // 3240.0 (810 * 4 weeks)
 */
export function calculatePeriodWageCap(
  category: NIBCategory,
  payIndicator: NIBPayIndicator,
  weeksWorked: number
): number {
  const categoryInfo = NIB_CATEGORY_INFO[category];
  if (!categoryInfo) {
    throw new Error(`Invalid category: ${category}. Must be 1-7.`);
  }

  if (payIndicator === 'W') {
    return categoryInfo.weeklyCap * weeksWorked;
  } else {
    return categoryInfo.monthlyCap;
  }
}

// ==================== Category Classification Helpers ====================

/**
 * Check if category is eligible for gratuity contributions
 *
 * @param category - NIB category (1-7)
 * @returns True if gratuity contributions apply
 *
 * @example
 * isGratuityEligible(NIBCategory.REGULAR); // true
 * isGratuityEligible(NIBCategory.RETIRED); // false
 */
export function isGratuityEligible(category: NIBCategory): boolean {
  return NIB_CATEGORY_INFO[category]?.gratuityEligible ?? false;
}

/**
 * Check if category is a retired category
 *
 * @param category - NIB category (1-7)
 * @returns True if category represents a retired employee
 *
 * @example
 * isRetiredCategory(NIBCategory.RETIRED); // true
 * isRetiredCategory(NIBCategory.REGULAR); // false
 */
export function isRetiredCategory(category: NIBCategory): boolean {
  return RETIRED_CATEGORIES.has(category);
}

/**
 * Check if category has zero employee contribution rate
 *
 * @param category - NIB category (1-7)
 * @returns True if employee rate is 0%
 *
 * @example
 * hasZeroEmployeeRate(NIBCategory.SUMMER_STUDENT); // true
 * hasZeroEmployeeRate(NIBCategory.REGULAR); // false
 */
export function hasZeroEmployeeRate(category: NIBCategory): boolean {
  return ZERO_EMPLOYEE_RATE_CATEGORIES.has(category);
}

// ==================== Category Display Helpers ====================

/**
 * Get display name for a category
 *
 * @param category - NIB category (1-7)
 * @returns Human-readable category name
 *
 * @example
 * getCategoryName(NIBCategory.SENIOR_65_69); // "Senior Employees (65-69)"
 */
export function getCategoryName(category: NIBCategory): string {
  return NIB_CATEGORY_INFO[category]?.name ?? 'Unknown Category';
}

/**
 * Get description for a category
 *
 * @param category - NIB category (1-7)
 * @returns Category description
 *
 * @example
 * getCategoryDescription(NIBCategory.SENIOR_65_69);
 * // "Employees 65-69 years old (not retired)"
 */
export function getCategoryDescription(category: NIBCategory): string {
  return NIB_CATEGORY_INFO[category]?.description ?? 'Unknown Category';
}

/**
 * Format a rate as a percentage string
 *
 * @param rate - Rate as decimal (e.g., 0.0465)
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted percentage string
 *
 * @example
 * formatRateAsPercentage(0.0465); // "4.65%"
 * formatRateAsPercentage(0.113, 1); // "11.3%"
 */
export function formatRateAsPercentage(
  rate: number,
  decimals: number = 2
): string {
  return `${(rate * 100).toFixed(decimals)}%`;
}

// ==================== Validation Helpers ====================

/**
 * Validate NIB category number
 *
 * @param category - Category to validate
 * @returns True if valid category (1-7)
 */
export function isValidCategory(category: number): category is NIBCategory {
  return (
    category >= NIB_VALIDATION.MIN_CATEGORY &&
    category <= NIB_VALIDATION.MAX_CATEGORY
  );
}

/**
 * Validate weeks worked for pay indicator
 *
 * @param weeks - Number of weeks
 * @param payIndicator - Pay frequency
 * @returns True if weeks is valid for the pay indicator
 */
export function isValidWeeksWorked(
  weeks: number,
  payIndicator: NIBPayIndicator
): boolean {
  if (weeks < NIB_VALIDATION.MIN_WEEKS_WORKED) {
    return false;
  }

  if (payIndicator === 'W') {
    return weeks <= NIB_VALIDATION.MAX_WEEKS_WEEKLY;
  } else {
    return weeks <= NIB_VALIDATION.MAX_WEEKS_MONTHLY;
  }
}

/**
 * Validate NI number format
 *
 * @param niNumber - National Insurance number string
 * @returns True if NI number has correct length
 */
export function isValidNINumber(niNumber: string): boolean {
  return niNumber.length === NIB_VALIDATION.NI_NUMBER_LENGTH;
}

// ==================== Calculation Helpers ====================

/**
 * Truncate wage to whole dollars (remove cents)
 *
 * @param wage - Original wage amount
 * @returns Truncated wage (whole dollars only)
 *
 * @example
 * truncateWage(1234.56); // 1234.00
 */
export function truncateWage(wage: number): number {
  return Math.floor(wage);
}

/**
 * Apply wage cap to insurable wages
 *
 * @param category - NIB category
 * @param payIndicator - Pay frequency
 * @param wage - Wage amount to cap
 * @param weeksWorked - Number of weeks
 * @returns Object with capped wage and whether cap was applied
 *
 * @example
 * const result = applyWageCap(NIBCategory.REGULAR, 'W', 5000, 4);
 * // { cappedWage: 3240, wasCapped: true, capAmount: 3240 }
 */
export function applyWageCap(
  category: NIBCategory,
  payIndicator: NIBPayIndicator,
  wage: number,
  weeksWorked: number
): {
  cappedWage: number;
  wasCapped: boolean;
  capAmount: number;
} {
  const capAmount = calculatePeriodWageCap(category, payIndicator, weeksWorked);
  const wasCapped = wage > capAmount;
  const cappedWage = Math.min(wage, capAmount);

  return {
    cappedWage,
    wasCapped,
    capAmount,
  };
}

/**
 * Calculate employee contribution
 *
 * @param insurableWages - Insurable wages (after truncation and capping)
 * @param category - NIB category
 * @returns Employee contribution amount
 *
 * @example
 * calculateEmployeeContribution(4050, NIBCategory.SENIOR_65_69);
 * // 168.075 (4050 * 0.0415)
 */
export function calculateEmployeeContribution(
  insurableWages: number,
  category: NIBCategory
): number {
  const { employeeRate } = getRatesForCategory(category);
  return insurableWages * employeeRate;
}

/**
 * Calculate employer contribution
 *
 * @param insurableWages - Insurable wages (after truncation and capping)
 * @param category - NIB category
 * @returns Employer contribution amount
 *
 * @example
 * calculateEmployerContribution(4050, NIBCategory.SENIOR_65_69);
 * // 249.075 (4050 * 0.0615)
 */
export function calculateEmployerContribution(
  insurableWages: number,
  category: NIBCategory
): number {
  const { employerRate } = getRatesForCategory(category);
  return insurableWages * employerRate;
}

/**
 * Calculate gratuity contribution
 *
 * @param gratuityWages - Gratuity/tips amount
 * @param category - NIB category
 * @returns Gratuity contribution amount (0 if not eligible)
 *
 * @example
 * calculateGratuityContribution(49, NIBCategory.REGULAR);
 * // 4.802 (49 * 0.098)
 *
 * calculateGratuityContribution(49, NIBCategory.RETIRED);
 * // 0 (retirees are not eligible)
 */
export function calculateGratuityContribution(
  gratuityWages: number,
  category: NIBCategory
): number {
  if (!isGratuityEligible(category)) {
    return 0;
  }
  return gratuityWages * 0.098; // 9.8% gratuity rate
}

/**
 * Round contribution amount to 2 decimal places
 *
 * @param amount - Contribution amount
 * @returns Rounded amount
 *
 * @example
 * roundContribution(168.075); // 168.08
 */
export function roundContribution(amount: number): number {
  return Math.round(amount * 100) / 100;
}

// ==================== Comprehensive Calculation ====================

/**
 * Calculate all NIB contributions for an employee
 *
 * @param params - Calculation parameters
 * @returns Complete breakdown of contributions
 *
 * @example
 * const result = calculateNIBContributions({
 *   grossWage: 4050.50,
 *   gratuityWages: 0,
 *   category: NIBCategory.SENIOR_65_69,
 *   payIndicator: 'W',
 *   weeksWorked: 5,
 * });
 */
export function calculateNIBContributions(params: {
  grossWage: number;
  gratuityWages?: number;
  category: NIBCategory;
  payIndicator: NIBPayIndicator;
  weeksWorked: number;
}): {
  originalWage: number;
  truncatedWage: number;
  cappedWage: number;
  insurableWages: number;
  employeeContribution: number;
  employerContribution: number;
  gratuityContribution: number;
  totalContribution: number;
  wasTruncated: boolean;
  wasCapped: boolean;
  capAmount: number;
} {
  const {
    grossWage,
    gratuityWages = 0,
    category,
    payIndicator,
    weeksWorked,
  } = params;

  // Step 1: Truncate wage to whole dollars
  const truncatedWage = truncateWage(grossWage);
  const wasTruncated = truncatedWage !== grossWage;

  // Step 2: Apply wage cap
  const { cappedWage, wasCapped, capAmount } = applyWageCap(
    category,
    payIndicator,
    truncatedWage,
    weeksWorked
  );

  // Step 3: Calculate contributions
  const employeeContrib = calculateEmployeeContribution(cappedWage, category);
  const employerContrib = calculateEmployerContribution(cappedWage, category);
  const gratuityContrib = calculateGratuityContribution(
    gratuityWages,
    category
  );

  // Step 4: Round all contributions
  const employeeContribution = roundContribution(employeeContrib);
  const employerContribution = roundContribution(employerContrib);
  const gratuityContribution = roundContribution(gratuityContrib);
  const totalContribution = roundContribution(
    employeeContrib + employerContrib + gratuityContrib
  );

  return {
    originalWage: grossWage,
    truncatedWage,
    cappedWage,
    insurableWages: cappedWage,
    employeeContribution,
    employerContribution,
    gratuityContribution,
    totalContribution,
    wasTruncated,
    wasCapped,
    capAmount,
  };
}
